import { useState, useMemo, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { DEFAULT_STRATEGIES, simulate, convertApyData } from '../lib/dsv-engine.js';
import { simulateRoyETH } from '../lib/royeth-engine.js';
import COLORS from '../lib/colors.js';
import rawYieldData from '../data/yield-data-90day.json';
import rawAcredDaily from '../data/ACRED_daily_nav_90d.json';
import rawMfOneNav from '../data/mF-ONE_NAV_History.json';
import rawFalconxNav from '../data/falconx-nav-interpolated.json';
import rawReusdNav from '../data/reusd_nav_history.json';
import rawSusdaiNav from '../data/susdai_nav_history.json';
import rawSusdsNav from '../data/susds_nav_history.json';
import rawRoyethRates from '../data/royeth-rate-data-prime.json';

// ── Formatting Helpers ──────────────────────────────────────────────────────

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + "%";
const fmtPctRaw = (v, d = 2) => v.toFixed(d) + "%";

// ── Strategy Colors (23 distinct) ───────────────────────────────────────────

const STRATEGY_COLORS = [
  '#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED',
  '#DB2777', '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
  '#BE185D', '#0D9488', '#CA8A04', '#9333EA', '#E11D48',
  '#0284C7', '#16A34A', '#C2410C', '#6D28D9', '#047857',
  '#B91C1C', '#1D4ED8', '#15803D',
];

// ── Reusable UI Components ──────────────────────────────────────────────────

const Toggle = ({ label, value, onChange, style: outerStyle }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", ...outerStyle }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: "relative", transition: "all 0.2s",
      flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: "absolute", top: 1,
        left: value ? 18 : 1,
        transition: "all 0.2s",
      }} />
    </div>
    {label && <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>{label}</span>}
  </div>
);

const Card = ({ title, color, children, collapsible, defaultCollapsed = false }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: "20px 24px",
      borderTop: `3px solid ${color || COLORS.accent}`,
    }}>
      <h3
        onClick={collapsible ? () => setCollapsed(!collapsed) : undefined}
        style={{
          fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          color: color || COLORS.accent, margin: "0 0 16px",
          fontFamily: "'Shippori Mincho B1', serif",
          cursor: collapsible ? "pointer" : "default",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        {title}
        {collapsible && (
          <span style={{ fontSize: 11, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}>
            {collapsed ? "+" : "\u2212"}
          </span>
        )}
      </h3>
      {!collapsed && children}
    </div>
  );
};

const SmallNumInput = ({ value, onChange, min = 0, max = 100, step = 0.1, width = 60 }) => (
  <input
    type="number"
    min={min}
    max={max}
    step={step}
    value={value}
    onChange={e => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) onChange(v);
    }}
    style={{
      width,
      background: COLORS.surfaceAlt,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 4,
      color: COLORS.textBody,
      fontFamily: "'Fragment Mono', monospace",
      fontSize: 11,
      padding: "3px 6px",
      textAlign: "right",
      boxSizing: "border-box",
    }}
  />
);

// ── Custom Tooltip for Chart ────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || payload.length === 0) return null;
  const fmt = formatter || (v => (v * 100).toFixed(3) + "%");
  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 8,
      padding: "10px 14px",
      fontSize: 11,
      fontFamily: "'Fragment Mono', monospace",
      maxHeight: 400,
      overflowY: "auto",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: COLORS.ink, fontFamily: "'Inter', sans-serif" }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 2 }}>
          <span style={{ color: entry.color, whiteSpace: "nowrap" }}>{entry.name}</span>
          <span style={{ color: COLORS.textBody }}>{fmt(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

export default function DsvSimulator() {
  // ── Strategy State ──
  const [strategies, setStrategies] = useState(() => DEFAULT_STRATEGIES.map(s => ({
    ...s,
    yieldShareToJunior: s.yieldShareToJunior || 0,
    yieldShareFee: s.yieldShareFee || 0,
    selfLiquidationBonus: s.selfLiquidationBonus || 0,
  })));

  // ── NAV Data State ──
  const [navDataText, setNavDataText] = useState("");
  const [useBuiltinData, setUseBuiltinData] = useState(true);

  // ── Simulation Parameters ──
  const [reversionDays, setReversionDays] = useState(7);
  const [liquidationBonusMode, setLiquidationBonusMode] = useState('pessimistic');
  const [liquidationRedemptionRate, setLiquidationRedemptionRate] = useState(0.3);

  // ── Chart Display ──
  const [showUnderlying, setShowUnderlying] = useState(false);
  const [apyShowUnderlying, setApyShowUnderlying] = useState(false);
  const [dsvOnly, setDsvOnly] = useState(false);

  // ── Tab State ──
  const [activeTab, setActiveTab] = useState('dsv');

  // ── RoyETH Config ──
  const [royethConfig, setRoyethConfig] = useState({
    aaveLTV: 39,
    useHistoricRates: true,
    stakingYieldOverride: 2.7,
    aaveSupplyOverride: 0.04,
    usdcBorrowOverride: 5.6,
    ltvMode: 'dynamic',
    maxDsvRedemption: 3,
  });
  const updateRoyethConfig = useCallback((field, value) => {
    setRoyethConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Config Export/Import ──
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");

  // ── Strategy Groups ──
  const dawnStrategies = useMemo(() =>
    strategies.map((s, i) => ({ ...s, idx: i })).filter(s => s.minCoverage > 0),
    [strategies]
  );
  const reserveStrategies = useMemo(() =>
    strategies.map((s, i) => ({ ...s, idx: i })).filter(s => s.minCoverage === 0),
    [strategies]
  );

  // ── Allocation Total ──
  const enabledAllocTotal = useMemo(() =>
    strategies.filter(s => s.enabled).reduce((sum, s) => sum + s.allocation, 0),
    [strategies]
  );

  // ── Strategy Update Helper ──
  const updateStrategy = useCallback((idx, field, value) => {
    setStrategies(prev => {
      const next = prev.map(s => ({ ...s }));
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, []);

  // ── Built-in data (converted once) ──
  const builtinNavData = useMemo(() => {
    const data = convertApyData(rawYieldData);
    // ACRED — daily NAV from RedStone Oracle (via Securitize API)
    const acredRows = rawAcredDaily.data;
    if (acredRows.length >= 2) {
      const baseNav = acredRows[0].nav_value_usd;
      data["Apollo ACRED (Securitize)"] = {
        dates: acredRows.map(d => d.date),
        navs: acredRows.map(d => d.nav_value_usd / baseNav),
      };
    }
    // mF-ONE — daily NAV from Fasanara
    if (rawMfOneNav.records && rawMfOneNav.records.length >= 2) {
      const baseNav = rawMfOneNav.records[0].nav_price;
      data["Fasanara mf-ONE"] = {
        dates: rawMfOneNav.records.map(d => d.date),
        navs: rawMfOneNav.records.map(d => d.nav_price / baseNav),
      };
    }
    // Pareto FalconX — interpolated from period-level APY data
    if (rawFalconxNav.length >= 2) {
      data["Pareto FalconX"] = {
        dates: rawFalconxNav.map(d => d.date),
        navs: rawFalconxNav.map(d => d.nav),
      };
    }
    // Re Protocol reUSD — on-chain share price
    if (rawReusdNav.records && rawReusdNav.records.length >= 2) {
      const baseNav = rawReusdNav.records[0].nav_price;
      data["Re Protocol reUSD"] = {
        dates: rawReusdNav.records.map(d => d.date),
        navs: rawReusdNav.records.map(d => d.nav_price / baseNav),
      };
    }
    // sUSDai — on-chain share price
    if (rawSusdaiNav.records && rawSusdaiNav.records.length >= 2) {
      const baseNav = rawSusdaiNav.records[0].nav_price;
      data["sUSDai"] = {
        dates: rawSusdaiNav.records.map(d => d.date),
        navs: rawSusdaiNav.records.map(d => d.nav_price / baseNav),
      };
    }
    // Sky sUSDS — on-chain share price
    if (rawSusdsNav.records && rawSusdsNav.records.length >= 2) {
      const baseNav = rawSusdsNav.records[0].nav_price;
      data["Reserve Sky sUSDS"] = {
        dates: rawSusdsNav.records.map(d => d.date),
        navs: rawSusdsNav.records.map(d => d.nav_price / baseNav),
      };
    }
    return data;
  }, []);

  // ── NAV Data Parsing ──
  const { navDataParsed, navParseError } = useMemo(() => {
    if (useBuiltinData) return { navDataParsed: builtinNavData, navParseError: null };
    if (!navDataText.trim()) return { navDataParsed: null, navParseError: null };
    try {
      const parsed = JSON.parse(navDataText);
      return { navDataParsed: parsed, navParseError: null };
    } catch (e) {
      return { navDataParsed: null, navParseError: e.message };
    }
  }, [useBuiltinData, builtinNavData, navDataText]);

  // ── Simulation ──
  const simResult = useMemo(() => {
    return simulate({
      strategies,
      navData: navDataParsed || {},
      simParams: { reversionDays, targetUtilization: 0.9, liquidationBonusMode, liquidationRedemptionRate },
    });
  }, [strategies, navDataParsed, reversionDays, liquidationBonusMode, liquidationRedemptionRate]);

  // ── RoyETH Simulation ──
  const royethResult = useMemo(() => {
    if (!simResult.dates || simResult.dates.length < 2) return null;
    return simulateRoyETH({
      dsvResult: simResult,
      rateData: rawRoyethRates,
      config: royethConfig,
    });
  }, [simResult, royethConfig]);

  // ── RoyETH Chart Data ──
  const royethChartData = useMemo(() => {
    if (!royethResult || !royethResult.dates.length) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      RoyETH: royethResult.royethVsEth[i],
      ETH: 1.0,
      wstETH: royethResult.wstEthVsEth[i],
    }));
  }, [royethResult]);

  const royethApyChartData = useMemo(() => {
    if (!royethResult || royethResult.dates.length < 8) return [];
    const w = 7;
    const pts = [];
    for (let i = w; i < royethResult.dates.length; i++) {
      const calDays = (Date.parse(royethResult.dates[i]) - Date.parse(royethResult.dates[i - w])) / 86400000;
      const annFactor = calDays > 0 ? 365 / calDays : 365 / w;
      const curNav = royethResult.royethVsEth[i];
      const prevNav = royethResult.royethVsEth[i - w];
      const date = royethResult.dates[i];
      pts.push({
        date,
        'RoyETH APY': (Math.pow(curNav / prevNav, annFactor) - 1) * 100,
      });
    }
    return pts;
  }, [royethResult]);

  const royethComponentChartData = useMemo(() => {
    if (!royethResult || !royethResult.dates.length) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      Staking: royethResult.components.staking[i],
      Supply: royethResult.components.supply[i],
      Carry: royethResult.components.carry[i],
    }));
  }, [royethResult]);

  const royethRatesChartData = useMemo(() => {
    if (!royethResult || !royethResult.dates.length) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      'DSV Senior Yield': royethResult.components.dsvYield[i],
      'USDC Borrow Rate': royethResult.components.borrowRate[i],
    }));
  }, [royethResult]);

  const actualLtvChartData = useMemo(() => {
    if (!royethResult || !royethResult.actualLTV || !royethResult.dates.length) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      'Actual LTV': royethResult.actualLTV[i],
    }));
  }, [royethResult]);

  const LIQUIDATION_LTV = 83;
  const liquidationDays = useMemo(() => {
    if (!royethResult || !royethResult.actualLTV) return [];
    return royethResult.dates.filter((_, i) => royethResult.actualLTV[i] >= LIQUIDATION_LTV);
  }, [royethResult]);

  // ── Chart Data ──
  const chartData = useMemo(() => {
    if (!simResult.dates || simResult.dates.length === 0) return [];
    return simResult.dates.map((date, i) => {
      const point = { date };
      // DSV aggregate
      point['DSV Aggregate'] = simResult.aggregate.dsvCumReturn[i];
      // Per-strategy
      for (const [name, data] of Object.entries(simResult.strategies)) {
        point[name] = data.seniorCumReturn[i];
        if (showUnderlying) {
          point[`${name} (underlying)`] = data.underlyingCumReturn[i];
        }
      }
      return point;
    });
  }, [simResult, showUnderlying]);

  // ── Trailing 7-Day APY Chart Data ──
  const apyChartData = useMemo(() => {
    if (!simResult.dates || simResult.dates.length < 8) return [];
    const w = 7; // window in data points
    const points = [];
    for (let i = w; i < simResult.dates.length; i++) {
      const point = { date: simResult.dates[i] };
      // Use actual calendar days for annualization (common dates may have gaps)
      const calDays = (Date.parse(simResult.dates[i]) - Date.parse(simResult.dates[i - w])) / 86400000;
      const annFactor = calDays > 0 ? 365 / calDays : 365 / w;
      for (const [name, data] of Object.entries(simResult.strategies)) {
        // Senior trailing 7d APY
        const curSr = 1 + data.seniorCumReturn[i];
        const prevSr = 1 + data.seniorCumReturn[i - w];
        point[`sr:${name}`] = Math.pow(curSr / prevSr, annFactor) - 1;
        // Underlying trailing 7d APY
        const curUl = 1 + data.underlyingCumReturn[i];
        const prevUl = 1 + data.underlyingCumReturn[i - w];
        point[`ul:${name}`] = Math.pow(curUl / prevUl, annFactor) - 1;
      }
      // DSV aggregate trailing 7d APY (senior only)
      const curAgg = 1 + simResult.aggregate.dsvCumReturn[i];
      const prevAgg = 1 + simResult.aggregate.dsvCumReturn[i - w];
      point['DSV Aggregate'] = Math.pow(curAgg / prevAgg, annFactor) - 1;
      points.push(point);
    }
    return points;
  }, [simResult]);

  // ── Enabled strategy names (for chart lines) ──
  const enabledNames = useMemo(() =>
    strategies.filter(s => s.enabled).map(s => s.name),
    [strategies]
  );

  // Strategy name -> color index mapping
  const strategyColorMap = useMemo(() => {
    const map = {};
    DEFAULT_STRATEGIES.forEach((s, i) => {
      map[s.name] = STRATEGY_COLORS[i % STRATEGY_COLORS.length];
    });
    return map;
  }, []);

  // ── Config Export/Import ──
  const getConfig = useCallback(() => ({
    strategies: strategies.map(s => ({
      name: s.name,
      enabled: s.enabled,
      allocation: s.allocation,
      yieldShareToJunior: s.yieldShareToJunior,
      minCoverage: s.minCoverage,
      seniorFee: s.seniorFee,
      juniorFee: s.juniorFee,
      yieldShareFee: s.yieldShareFee,
      selfLiquidationBonus: s.selfLiquidationBonus,
      underlyingYield: s.underlyingYield,
      finalizedLossThreshold: s.finalizedLossThreshold,
      recoveryDuration: s.recoveryDuration,
    })),
    reversionDays,
    liquidationBonusMode,
    liquidationRedemptionRate,
    royethConfig,
  }), [strategies, reversionDays, liquidationBonusMode, liquidationRedemptionRate, royethConfig]);

  const configJson = useMemo(() => JSON.stringify(getConfig(), null, 2), [getConfig]);

  const handleCopy = useCallback(() => {
    try {
      const ta = document.createElement("textarea");
      ta.value = configJson;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch { /* ignore */ }
    setShowExport(!showExport);
    setShowImport(false);
  }, [configJson, showExport]);

  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const [saveFlash, setSaveFlash] = useState(false);
  const handleSave = useCallback(() => {
    fetch('/api/save-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: configJson,
    })
      .then(r => r.json())
      .then(res => {
        if (res.ok) {
          setSaveFlash(true);
          setTimeout(() => setSaveFlash(false), 1200);
        } else {
          alert('Save failed: ' + (res.error || 'unknown error'));
        }
      })
      .catch(err => alert('Save failed: ' + err.message));
  }, [configJson]);

  const handleImport = useCallback(() => {
    try {
      const cfg = JSON.parse(importText);
      if (cfg.strategies && Array.isArray(cfg.strategies)) {
        setStrategies(prev => prev.map(s => {
          const imported = cfg.strategies.find(cs => cs.name === s.name);
          if (!imported) return s;
          return {
            ...s,
            enabled: imported.enabled != null ? imported.enabled : s.enabled,
            allocation: imported.allocation != null ? imported.allocation : s.allocation,
            yieldShareToJunior: imported.yieldShareToJunior != null ? imported.yieldShareToJunior : s.yieldShareToJunior,
            minCoverage: imported.minCoverage != null ? imported.minCoverage : s.minCoverage,
            seniorFee: imported.seniorFee != null ? imported.seniorFee : s.seniorFee,
            juniorFee: imported.juniorFee != null ? imported.juniorFee : s.juniorFee,
            yieldShareFee: imported.yieldShareFee != null ? imported.yieldShareFee : s.yieldShareFee,
            selfLiquidationBonus: imported.selfLiquidationBonus != null ? imported.selfLiquidationBonus : s.selfLiquidationBonus,
            underlyingYield: imported.underlyingYield != null ? imported.underlyingYield : s.underlyingYield,
            finalizedLossThreshold: imported.finalizedLossThreshold != null ? imported.finalizedLossThreshold : s.finalizedLossThreshold,
            recoveryDuration: imported.recoveryDuration != null ? imported.recoveryDuration : s.recoveryDuration,
          };
        }));
      }
      if (cfg.reversionDays != null) setReversionDays(cfg.reversionDays);
      if (cfg.liquidationBonusMode != null) setLiquidationBonusMode(cfg.liquidationBonusMode);
      if (cfg.liquidationRedemptionRate != null) setLiquidationRedemptionRate(cfg.liquidationRedemptionRate);
      if (cfg.royethConfig != null) setRoyethConfig(prev => ({ ...prev, ...cfg.royethConfig }));
      setShowImport(false);
      setImportText("");
    } catch {
      alert("Invalid JSON config");
    }
  }, [importText]);

  // ── Dawn Strategy Row Renderer ──
  const dawnGridCols = "36px 1fr 80px 90px 80px 80px 80px 80px 80px 80px 100px";
  const renderDawnRow = useCallback((s) => {
    const idx = s.idx;
    const color = strategyColorMap[s.name] || COLORS.textBody;
    return (
      <div key={s.name} style={{
        display: "grid",
        gridTemplateColumns: dawnGridCols,
        gap: 6,
        alignItems: "center",
        padding: "6px 0",
        borderBottom: `1px solid ${COLORS.borderLight}`,
        opacity: s.enabled ? 1 : 0.4,
      }}>
        <Toggle value={s.enabled} onChange={v => updateStrategy(idx, 'enabled', v)} />
        <div style={{
          fontSize: 12, color: s.enabled ? color : COLORS.textTertiary,
          fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }} title={s.name}>
          {s.name}
        </div>
        <SmallNumInput
          value={+(s.allocation * 100).toFixed(2)}
          onChange={v => updateStrategy(idx, 'allocation', v / 100)}
          min={0} max={100} step={0.5}
        />
        <SmallNumInput
          value={+(s.minCoverage * 100).toFixed(2)}
          onChange={v => updateStrategy(idx, 'minCoverage', v / 100)}
          min={0} max={50} step={0.5}
        />
        <SmallNumInput
          value={+((s.yieldShareToJunior || 0) * 100).toFixed(2)}
          onChange={v => updateStrategy(idx, 'yieldShareToJunior', v / 100)}
          min={0} max={100} step={0.5}
        />
        <SmallNumInput
          value={+(s.seniorFee * 100).toFixed(1)}
          onChange={v => updateStrategy(idx, 'seniorFee', v / 100)}
          min={0} max={50} step={0.5}
        />
        <SmallNumInput
          value={+(s.juniorFee * 100).toFixed(1)}
          onChange={v => updateStrategy(idx, 'juniorFee', v / 100)}
          min={0} max={50} step={0.5}
        />
        <SmallNumInput
          value={+(s.yieldShareFee * 100).toFixed(1)}
          onChange={v => updateStrategy(idx, 'yieldShareFee', v / 100)}
          min={0} max={50} step={0.5}
        />
        <SmallNumInput
          value={+(s.selfLiquidationBonus * 100).toFixed(1)}
          onChange={v => updateStrategy(idx, 'selfLiquidationBonus', v / 100)}
          min={0} max={50} step={0.5}
        />
        <SmallNumInput
          value={s.recoveryDuration != null ? s.recoveryDuration : 7}
          onChange={v => updateStrategy(idx, 'recoveryDuration', Math.max(0, Math.round(v)))}
          min={0} max={365} step={1}
        />
        <SmallNumInput
          value={+(((s.finalizedLossThreshold != null ? s.finalizedLossThreshold : 0.05)) * 100).toFixed(1)}
          onChange={v => updateStrategy(idx, 'finalizedLossThreshold', v / 100)}
          min={0} max={50} step={0.5}
        />
      </div>
    );
  }, [strategyColorMap, updateStrategy]);

  // ── Reserve Strategy Row Renderer ──
  const reserveGridCols = "36px 1fr 72px";
  const renderReserveRow = useCallback((s) => {
    const idx = s.idx;
    const color = strategyColorMap[s.name] || COLORS.textBody;
    return (
      <div key={s.name} style={{
        display: "grid",
        gridTemplateColumns: reserveGridCols,
        gap: 6,
        alignItems: "center",
        padding: "6px 0",
        borderBottom: `1px solid ${COLORS.borderLight}`,
        opacity: s.enabled ? 1 : 0.4,
      }}>
        <Toggle value={s.enabled} onChange={v => updateStrategy(idx, 'enabled', v)} />
        <div style={{
          fontSize: 12, color: s.enabled ? color : COLORS.textTertiary,
          fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }} title={s.name}>
          {s.name}
        </div>
        <SmallNumInput
          value={+(s.allocation * 100).toFixed(2)}
          onChange={v => updateStrategy(idx, 'allocation', v / 100)}
          min={0} max={100} step={0.5}
        />
      </div>
    );
  }, [strategyColorMap, updateStrategy]);

  // Column header row for Dawn Markets
  const dawnColumnHeader = (
    <div style={{
      display: "grid",
      gridTemplateColumns: dawnGridCols,
      gap: 6,
      padding: "0 0 6px",
      borderBottom: `1px solid ${COLORS.border}`,
    }}>
      <div />
      <div style={colHeaderStyle}>Strategy</div>
      <div style={colHeaderStyle}>Allocation %</div>
      <div style={colHeaderStyle}>Min Coverage %</div>
      <div style={colHeaderStyle}>Yield Share %</div>
      <div style={colHeaderStyle}>Senior Fee %</div>
      <div style={colHeaderStyle}>Junior Fee %</div>
      <div style={colHeaderStyle}>YS Fee %</div>
      <div style={colHeaderStyle}>Liq. Bonus %</div>
      <div style={colHeaderStyle}>Max Recovery Duration (days)</div>
      <div style={colHeaderStyle}>Finalized Junior Loss Threshold %</div>
    </div>
  );

  // Column header row for Reserve Strategies
  const reserveColumnHeader = (
    <div style={{
      display: "grid",
      gridTemplateColumns: reserveGridCols,
      gap: 6,
      padding: "0 0 6px",
      borderBottom: `1px solid ${COLORS.border}`,
    }}>
      <div />
      <div style={colHeaderStyle}>Strategy</div>
      <div style={colHeaderStyle}>Allocation %</div>
    </div>
  );

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: COLORS.bg,
      color: COLORS.ink,
      minHeight: "100vh",
      padding: "24px 20px",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Shippori+Mincho+B1:wght@400;500;600;700;800&family=Fragment+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
  input[type=number] {
    -moz-appearance: textfield;
  }
  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto" }}>

        {/* ── Editorial Header ── */}
        <div style={{ position: "relative", marginBottom: 24 }}>
          <div style={{
            position: "absolute", top: 0, right: 0,
            fontSize: 11, fontWeight: 700, letterSpacing: "0.25em", textTransform: "uppercase",
            color: COLORS.textSecondary, fontFamily: "'Shippori Mincho B1', serif",
          }}>
            ROYCO
          </div>
          <h1 style={{
            fontSize: 32, fontWeight: 700, margin: "0 0 12px",
            letterSpacing: "-0.02em", lineHeight: 1.15,
            fontFamily: "'Shippori Mincho B1', serif", color: COLORS.ink,
          }}>
            DSV Yield Simulator
          </h1>
          <div style={{ width: 40, height: 2, background: COLORS.accent, marginBottom: 12 }} />
          <p style={{
            fontSize: 14, color: COLORS.textSecondary, margin: "0 0 4px",
            fontFamily: "'Shippori Mincho B1', serif",
          }}>
            Strategy-level yield simulation with junior tranche drawdown protection
          </p>
          <p style={{ fontSize: 12, color: COLORS.textTertiary, margin: 0 }}>
            {enabledNames.length} strategies enabled
            {" \u2022 "}
            {simResult.aggregate.summary.days || 365} day simulation
          </p>

          {/* Save (localhost only) / Export / Import Buttons */}
          <div style={{ position: "absolute", bottom: 0, right: 0, display: "flex", gap: 8 }}>
            {isLocal && (
              <button
                onClick={handleSave}
                style={{
                  background: saveFlash ? COLORS.positive : COLORS.surface,
                  border: `1px solid ${saveFlash ? COLORS.positive : COLORS.border}`,
                  borderRadius: 8,
                  color: saveFlash ? COLORS.bg : COLORS.textSecondary,
                  fontSize: 11, fontWeight: 600, padding: "6px 14px", cursor: "pointer",
                  transition: "all 0.2s", letterSpacing: "0.04em",
                }}
              >
                {saveFlash ? "Saved!" : "Save Config"}
              </button>
            )}
            <button
              onClick={handleCopy}
              style={{
                background: showExport ? COLORS.accent : COLORS.surface,
                border: `1px solid ${showExport ? COLORS.accent : COLORS.border}`,
                borderRadius: 8,
                color: showExport ? COLORS.bg : COLORS.textSecondary,
                fontSize: 11, fontWeight: 600, padding: "6px 14px", cursor: "pointer",
                transition: "all 0.2s", letterSpacing: "0.04em",
              }}
            >
              Export Config
            </button>
            <button
              onClick={() => { setShowImport(!showImport); setShowExport(false); }}
              style={{
                background: showImport ? COLORS.accent : COLORS.surface,
                border: `1px solid ${showImport ? COLORS.accent : COLORS.border}`,
                borderRadius: 8,
                color: showImport ? COLORS.bg : COLORS.textSecondary,
                fontSize: 11, fontWeight: 600, padding: "6px 14px", cursor: "pointer",
                transition: "all 0.2s", letterSpacing: "0.04em",
              }}
            >
              Import Config
            </button>
          </div>
        </div>

        {/* ── Tab Selector ── */}
        <div style={{
          display: "flex", gap: 0, marginBottom: 20,
          borderBottom: `1px solid ${COLORS.border}`,
        }}>
          {[
            { key: 'dsv', label: 'DSV Senior' },
            { key: 'royeth', label: 'RoyETH Carry' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: "transparent",
                border: "none",
                borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : `2px solid transparent`,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 700 : 500,
                fontFamily: "'Shippori Mincho B1', serif",
                color: activeTab === tab.key ? COLORS.ink : COLORS.textTertiary,
                cursor: "pointer",
                letterSpacing: "0.04em",
                transition: "all 0.15s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════ DSV Tab ═══════════════ */}
        {activeTab === 'dsv' && (<>

        {/* ── Export Panel ── */}
        {showExport && (
          <div style={{
            marginBottom: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: 16,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Current Config -- copied to clipboard
              </div>
              <button
                onClick={() => setShowExport(false)}
                style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6,
                  color: COLORS.textTertiary, fontSize: 11, fontWeight: 600, padding: "4px 12px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
            <pre
              onClick={e => {
                const range = document.createRange();
                range.selectNodeContents(e.target);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
              }}
              style={{
                background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8,
                color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace", fontSize: 11,
                padding: 12, margin: 0, cursor: "pointer", whiteSpace: "pre-wrap",
                userSelect: "all", maxHeight: 300, overflowY: "auto",
              }}
            >{configJson}</pre>
          </div>
        )}

        {/* ── Import Panel ── */}
        {showImport && (
          <div style={{
            marginBottom: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
              Paste JSON Config
            </div>
            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder='{"strategies": [...], "reversionDays": 7}'
              style={{
                width: "100%", minHeight: 80, background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace",
                fontSize: 11, padding: 12, resize: "vertical", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                onClick={handleImport}
                style={{
                  background: COLORS.accent, border: "none", borderRadius: 6,
                  color: COLORS.bg, fontSize: 11, fontWeight: 600, padding: "6px 16px",
                  cursor: "pointer",
                }}
              >
                Load Config
              </button>
              <button
                onClick={() => { setShowImport(false); setImportText(""); }}
                style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6,
                  color: COLORS.textTertiary, fontSize: 11, fontWeight: 600, padding: "6px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Aggregate Metrics ── */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20,
        }}>
          <MetricCard
            label="DSV Annualized Return"
            value={fmtPct(simResult.aggregate.summary.annualizedReturn)}
            color={simResult.aggregate.summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative}
          />
          <MetricCard
            label="DSV Total Return"
            value={fmtPct(simResult.aggregate.summary.totalReturn)}
            color={simResult.aggregate.summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative}
          />
          <MetricCard
            label="Simulation Days"
            value={String(simResult.aggregate.summary.days || 0)}
            color={COLORS.ink}
          />
          <MetricCard
            label="Enabled Strategies"
            value={String(enabledNames.length)}
            color={COLORS.accent}
          />
        </div>

        {/* ── Cumulative Return Chart ── */}
        <Card title="Cumulative Senior Return" color={COLORS.accent}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            <Toggle label="DSV Only" value={dsvOnly} onChange={setDsvOnly} />
            {!dsvOnly && <Toggle label="Show Underlying Returns" value={showUnderlying} onChange={setShowUnderlying} />}
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={480}>
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  tickFormatter={d => d.slice(5)}
                  interval={Math.max(1, Math.floor(chartData.length / 12))}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  tickFormatter={v => (v * 100).toFixed(1) + "%"}
                  width={60}
                />
                <Tooltip content={<ChartTooltip />} />
                {!dsvOnly && enabledNames.map((name) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={strategyColorMap[name]}
                    strokeWidth={1.2}
                    dot={false}
                    name={name}
                  />
                ))}
                {!dsvOnly && showUnderlying && enabledNames.map((name) => (
                  <Line
                    key={`${name}-underlying`}
                    type="monotone"
                    dataKey={`${name} (underlying)`}
                    stroke={strategyColorMap[name]}
                    strokeWidth={1}
                    strokeDasharray="4 3"
                    dot={false}
                    name={`${name} (underlying)`}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="DSV Aggregate"
                  stroke={COLORS.ink}
                  strokeWidth={3}
                  dot={false}
                  name="DSV Aggregate"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
              No enabled strategies to chart.
            </div>
          )}
        </Card>

        {/* ── Trailing 7-Day APY Chart ── */}
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Card title="Trailing 7-Day APY" color={COLORS.accent}>
            <div style={{ display: "inline-grid", gridTemplateColumns: "auto auto auto", gap: "8px 6px", alignItems: "center", marginBottom: 12 }}>
              <span />
              <Toggle value={dsvOnly} onChange={setDsvOnly} />
              <span style={{
                fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase",
                color: COLORS.textSecondary, cursor: "pointer", userSelect: "none",
              }} onClick={() => setDsvOnly(!dsvOnly)}>DSV Only</span>
              {!dsvOnly && <>
                <span style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase",
                  color: apyShowUnderlying ? COLORS.textTertiary : COLORS.textSecondary, textAlign: "right",
                }}>Senior</span>
                <Toggle value={apyShowUnderlying} onChange={setApyShowUnderlying} />
                <span style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase",
                  color: apyShowUnderlying ? COLORS.textSecondary : COLORS.textTertiary,
                }}>Underlying</span>
              </>}
            </div>
            {apyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={apyChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={d => d.slice(5)}
                    interval={Math.max(1, Math.floor(apyChartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={v => (v * 100).toFixed(1) + "%"}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  {!dsvOnly && enabledNames.map((name) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={`${apyShowUnderlying ? 'ul' : 'sr'}:${name}`}
                      stroke={strategyColorMap[name]}
                      strokeWidth={1.2}
                      dot={false}
                      name={name}
                    />
                  ))}
                  {(!dsvOnly ? !apyShowUnderlying : true) && (
                    <Line
                      type="monotone"
                      dataKey="DSV Aggregate"
                      stroke={COLORS.ink}
                      strokeWidth={3}
                      dot={false}
                      name="DSV Aggregate"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
                Not enough data for 7-day trailing average (need at least 8 days).
              </div>
            )}
          </Card>
        </div>

        {/* ── Strategy Configuration Panel ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 20 }}>
          {/* Allocation Summary Bar */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10,
            padding: "12px 20px",
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>
              Enabled Allocation Total:
            </span>
            <span style={{
              fontSize: 18, fontWeight: 800,
              fontFamily: "'Fragment Mono', monospace",
              color: enabledAllocTotal > 1
                ? COLORS.negative
                : Math.abs(enabledAllocTotal - 1) < 0.001
                  ? COLORS.positive
                  : COLORS.caution,
            }}>
              {fmtPctRaw(enabledAllocTotal * 100, 1)}
            </span>
            <span style={{ fontSize: 11, color: COLORS.textTertiary }}>
              ({strategies.filter(s => s.enabled).length} strategies)
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => {
                const enabled = strategies.filter(s => s.enabled);
                const equal = 1 / enabled.length;
                setStrategies(prev => prev.map(s => s.enabled ? { ...s, allocation: equal } : s));
              }}
              style={{
                background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                color: COLORS.textSecondary, fontSize: 10, fontWeight: 600, padding: "4px 10px",
                cursor: "pointer", letterSpacing: "0.04em",
              }}
            >
              Equalize
            </button>
          </div>

          {/* Dawn Markets */}
          <Card title={`Dawn Markets (${dawnStrategies.length} strategies)`} color={COLORS.accent} collapsible defaultCollapsed={false}>
            {dawnColumnHeader}
            {dawnStrategies.map(renderDawnRow)}
          </Card>

          {/* Reserve Strategies */}
          <Card title={`Reserve Strategies (${reserveStrategies.length} strategies)`} color={COLORS.champagne} collapsible defaultCollapsed={false}>
            {reserveColumnHeader}
            {reserveStrategies.map(renderReserveRow)}
          </Card>
        </div>

        {/* ── Simulation Parameters + NAV Data ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {/* Sim Params */}
          <Card title="Simulation Parameters" color={COLORS.accentAlt}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>
                Reversion Period (days)
              </label>
              <SmallNumInput
                value={reversionDays}
                onChange={v => setReversionDays(Math.max(1, Math.round(v)))}
                min={1} max={90} step={1} width={50}
              />
            </div>
            <div style={{ fontSize: 10, color: COLORS.textTertiary }}>
              Days to fully rebuild JT from zero to target J:S ratio. Partial depletions recover proportionally faster.
            </div>
            <div style={{ marginTop: 12, borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>
                  Liquidation Bonus Mode
                </label>
                <select
                  value={liquidationBonusMode}
                  onChange={e => setLiquidationBonusMode(e.target.value)}
                  style={{
                    background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                    color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: "4px 8px",
                  }}
                >
                  <option value="pessimistic">Pessimistic (100% ST redeems)</option>
                  <option value="optimistic">Optimistic (partial ST redeems)</option>
                </select>
              </div>
              {liquidationBonusMode === 'optimistic' && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>
                    ST Redemption Rate %
                  </label>
                  <SmallNumInput
                    value={+(liquidationRedemptionRate * 100).toFixed(0)}
                    onChange={v => setLiquidationRedemptionRate(v / 100)}
                    min={0} max={100} step={5} width={50}
                  />
                </div>
              )}
              <div style={{ fontSize: 10, color: COLORS.textTertiary }}>
                At LLTV breach or timer expiry, ST redeemers receive a bonus from JT. Pessimistic assumes all ST exits; optimistic uses the configured redemption rate.
              </div>
            </div>
          </Card>

          {/* NAV Data */}
          <Card title="Yield Data" color={COLORS.accentAlt}>
            <Toggle
              label="Use built-in 90-day APY data"
              value={useBuiltinData}
              onChange={setUseBuiltinData}
              style={{ marginBottom: 10 }}
            />
            {useBuiltinData ? (
              <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
                <div style={{ color: COLORS.positive, fontWeight: 600, marginBottom: 4 }}>
                  Loaded {Object.keys(builtinNavData).length} strategies from bundled APY data
                </div>
                <div style={{ fontSize: 10, color: COLORS.textTertiary }}>
                  Maple Institutional uses synthetic NAV from configured underlying yield (historic data still needed).
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 8 }}>
                  Paste historical NAV data as JSON. Format: <code style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10 }}>{`{"Strategy Name": {"dates":["2025-01-01",...], "navs":[1.0,...]}}`}</code>
                </div>
                <textarea
                  value={navDataText}
                  onChange={e => setNavDataText(e.target.value)}
                  placeholder='{"Fasanara mf-ONE": {"dates": ["2025-01-01","2025-01-02"], "navs": [1.0, 1.00025]}}'
                  style={{
                    width: "100%", minHeight: 80, background: COLORS.surfaceAlt, border: `1px solid ${navParseError ? COLORS.negative : COLORS.border}`,
                    borderRadius: 8, color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace",
                    fontSize: 11, padding: 12, resize: "vertical", boxSizing: "border-box",
                  }}
                />
                {navParseError && (
                  <div style={{ fontSize: 10, color: COLORS.negative, marginTop: 4 }}>
                    Parse error: {navParseError}
                  </div>
                )}
                {navDataParsed && !navParseError && (
                  <div style={{ fontSize: 10, color: COLORS.positive, marginTop: 4 }}>
                    Loaded NAV data for {Object.keys(navDataParsed).length} strategies
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {/* ── Summary Table ── */}
        <div style={{ marginTop: 20 }}>
          <Card title="Strategy Summary" color={COLORS.accentAlt}>
            <div style={{ overflowX: "auto" }}>
              <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: 12,
                fontFamily: "'Fragment Mono', monospace",
              }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                    <th style={thStyle}>Strategy</th>
                    <th style={thRightStyle}>Weight</th>
                    <th style={thRightStyle}>Ann.</th>
                    <th style={thRightStyle}>Total Return</th>
                    <th style={thRightStyle}>Max DD to Senior</th>
                    <th style={thRightStyle}>J:S Ratio</th>
                    <th style={thRightStyle}>Latest JT Depletion</th>
                    <th style={thRightStyle}>FLT</th>
                    <th style={thRightStyle}>LLTV</th>
                    <th style={thRightStyle}>Fixed-Term Days</th>
                    <th style={thRightStyle}>Junior Loss Days</th>
                    <th style={thRightStyle}>Senior Loss Days</th>
                  </tr>
                </thead>
                <tbody>
                  {enabledNames.map((name) => {
                    const s = simResult.strategies[name];
                    if (!s) return null;
                    const { summary } = s;
                    // Compute max drawdown to senior from cumulative return series
                    let maxDD = 0;
                    let peak = 0;
                    for (let i = 0; i < s.seniorCumReturn.length; i++) {
                      const val = s.seniorCumReturn[i];
                      if (val > peak) peak = val;
                      const dd = peak - val;
                      if (dd > maxDD) maxDD = dd;
                    }
                    // JT depletion: final jtEffectiveNav vs 1.0
                    const jtFinal = s.jtEffectiveNav[s.jtEffectiveNav.length - 1];
                    const jtDepletion = summary.jsRatioInitial > 0 ? Math.max(0, 1 - jtFinal) : 0;
                    // Count days where senior cumulative return decreased
                    let seniorLossDays = 0;
                    for (let i = 1; i < s.seniorCumReturn.length; i++) {
                      if (s.seniorCumReturn[i] < s.seniorCumReturn[i - 1]) seniorLossDays++;
                    }
                    const color = strategyColorMap[name];

                    return (
                      <tr key={name} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                        <td style={{ ...tdStyle, color, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{name}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{fmtPct(summary.weight)}</td>
                        <td style={{ ...tdStyle, textAlign: "right", color: summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative }}>
                          {fmtPct(summary.annualizedReturn)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative }}>
                          {fmtPct(summary.totalReturn, 3)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: maxDD > 0 ? COLORS.negative : COLORS.textBody }}>
                          {maxDD > 0 ? `-${fmtPct(maxDD, 3)}` : "0.00%"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {summary.jsRatioInitial > 0 ? summary.jsRatioInitial.toFixed(4) : "--"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: jtDepletion > 0 ? COLORS.negative : COLORS.textBody }}>
                          {summary.isReserve ? "--" : fmtPct(jtDepletion, 2)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {summary.isReserve ? "--" : fmtPct(summary.finalizedLossThreshold || 0)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {summary.isReserve ? "--" : (s.lltv != null ? s.lltv.toFixed(4) : "--")}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: summary.fixedTermDays > 0 ? COLORS.caution : COLORS.textBody }}>
                          {summary.isReserve ? "--" : summary.fixedTermDays}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: summary.lossBookings > 0 ? COLORS.negative : COLORS.textBody }}>
                          {summary.isReserve ? "--" : summary.lossBookings}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", color: seniorLossDays > 0 ? COLORS.negative : COLORS.textBody }}>
                          {summary.isReserve ? "--" : seniorLossDays}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Aggregate DSV Row */}
                  <tr style={{ borderTop: `2px solid ${COLORS.border}`, fontWeight: 700 }}>
                    <td style={{ ...tdStyle, fontFamily: "'Inter', sans-serif", fontWeight: 800, color: COLORS.ink }}>DSV Aggregate</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>100%</td>
                    <td style={{ ...tdStyle, textAlign: "right", color: simResult.aggregate.summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative }}>
                      {fmtPct(simResult.aggregate.summary.annualizedReturn)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", color: simResult.aggregate.summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative }}>
                      {fmtPct(simResult.aggregate.summary.totalReturn, 3)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {(() => {
                        let maxDD = 0, peak = 0;
                        for (let i = 0; i < simResult.aggregate.dsvCumReturn.length; i++) {
                          const val = simResult.aggregate.dsvCumReturn[i];
                          if (val > peak) peak = val;
                          const dd = peak - val;
                          if (dd > maxDD) maxDD = dd;
                        }
                        return maxDD > 0 ? <span style={{ color: COLORS.negative }}>-{fmtPct(maxDD, 3)}</span> : "0.00%";
                      })()}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>--</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        </>)}

        {/* ═══════════════ RoyETH Carry Tab ═══════════════ */}
        {activeTab === 'royeth' && royethResult && (<>

        {/* ── RoyETH Config Panel ── */}
        <div style={{ marginBottom: 20 }}>
        <Card title="RoyETH Configuration" color={COLORS.accent}>
          {/* LTV Mode Selector */}
          <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `1px solid ${COLORS.borderLight}`, paddingBottom: 12 }}>
            {['fixed', 'dynamic'].map(mode => (
              <button
                key={mode}
                onClick={() => updateRoyethConfig('ltvMode', mode)}
                style={{
                  background: royethConfig.ltvMode === mode ? COLORS.accent : 'transparent',
                  border: `1px solid ${royethConfig.ltvMode === mode ? COLORS.accent : COLORS.border}`,
                  borderRadius: mode === 'fixed' ? '6px 0 0 6px' : '0 6px 6px 0',
                  color: royethConfig.ltvMode === mode ? COLORS.bg : COLORS.textSecondary,
                  fontSize: 11, fontWeight: 600, padding: "6px 16px", cursor: "pointer",
                  letterSpacing: "0.04em", textTransform: "uppercase",
                  transition: "all 0.15s",
                }}
              >
                {mode === 'fixed' ? 'Fixed LTV' : 'Dynamic LTV'}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 14, lineHeight: 1.5 }}>
            {royethConfig.ltvMode === 'fixed'
              ? 'Assumes LTV is perfectly maintained at the target throughout the simulation — no rebalancing friction or redemption delays.'
              : `Rebalances daily toward target LTV. When ETH rises, borrows more USDC and deposits into DSV instantly. When ETH falls, redeems DSV to repay debt, capped at ${royethConfig.maxDsvRedemption.toFixed(1)}% of the DSV position per day — so actual LTV can drift above target during sustained drawdowns.`
            }
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                {royethConfig.ltvMode === 'dynamic' ? 'Target' : 'Aave'} LTV: {royethConfig.aaveLTV}%
              </div>
              <input
                type="range" min={10} max={75} step={1}
                value={royethConfig.aaveLTV}
                onChange={e => updateRoyethConfig('aaveLTV', +e.target.value)}
                style={{ width: "100%", accentColor: COLORS.accent }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textTertiary }}>
                <span>10%</span><span>75%</span>
              </div>
              {royethConfig.ltvMode === 'dynamic' && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                    Max DSV Redemption / Day: {royethConfig.maxDsvRedemption.toFixed(1)}%
                  </div>
                  <input
                    type="range" min={0} max={10} step={0.5}
                    value={royethConfig.maxDsvRedemption}
                    onChange={e => updateRoyethConfig('maxDsvRedemption', +e.target.value)}
                    style={{ width: "100%", accentColor: COLORS.accent }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textTertiary }}>
                    <span>0%</span><span>10%</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Toggle
                label="Use Historical Rates"
                value={royethConfig.useHistoricRates}
                onChange={v => updateRoyethConfig('useHistoricRates', v)}
              />
              {!royethConfig.useHistoricRates && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                      Staking Yield: {royethConfig.stakingYieldOverride.toFixed(2)}%
                    </div>
                    <input
                      type="range" min={0} max={10} step={0.05}
                      value={royethConfig.stakingYieldOverride}
                      onChange={e => updateRoyethConfig('stakingYieldOverride', +e.target.value)}
                      style={{ width: "100%", accentColor: COLORS.accent }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                      Supply APY: {royethConfig.aaveSupplyOverride.toFixed(2)}%
                    </div>
                    <input
                      type="range" min={0} max={2} step={0.01}
                      value={royethConfig.aaveSupplyOverride}
                      onChange={e => updateRoyethConfig('aaveSupplyOverride', +e.target.value)}
                      style={{ width: "100%", accentColor: COLORS.accent }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                      USDC Borrow Rate: {royethConfig.usdcBorrowOverride.toFixed(2)}%
                    </div>
                    <input
                      type="range" min={0} max={15} step={0.05}
                      value={royethConfig.usdcBorrowOverride}
                      onChange={e => updateRoyethConfig('usdcBorrowOverride', +e.target.value)}
                      style={{ width: "100%", accentColor: COLORS.accent }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {royethConfig.ltvMode === 'dynamic' && liquidationDays.length > 0 && (
            <div style={{
              marginTop: 14, padding: "10px 14px",
              background: COLORS.negativeBg,
              border: `1px solid ${COLORS.negativeBorder}`,
              borderRadius: 8,
              fontSize: 11, color: COLORS.negative, lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 700 }}>Liquidation warning:</span>{' '}
              Position would have been liquidated on {liquidationDays.length} day{liquidationDays.length !== 1 ? 's' : ''}{' '}
              ({liquidationDays.length <= 5
                ? liquidationDays.join(', ')
                : `${liquidationDays.slice(0, 3).join(', ')} ... ${liquidationDays[liquidationDays.length - 1]}`
              })
            </div>
          )}
        </Card>
        </div>

        {/* ── RoyETH Metric Cards ── */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20,
        }}>
          <DualMetricCard
            title="RoyETH vs ETH"
            leftLabel="Total"
            leftValue={(royethResult.summary.totalReturn >= 0 ? "+" : "") + fmtPct(royethResult.summary.totalReturn)}
            leftColor={royethResult.summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative}
            rightLabel="Annualized"
            rightValue={(royethResult.summary.annualizedReturn >= 0 ? "+" : "") + fmtPct(royethResult.summary.annualizedReturn)}
            rightColor={royethResult.summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative}
          />
          <DualMetricCard
            title="RoyETH vs wstETH"
            leftLabel="Total"
            leftValue={(royethResult.summary.vsWstEth >= 0 ? "+" : "") + fmtPct(royethResult.summary.vsWstEth)}
            leftColor={royethResult.summary.vsWstEth >= 0 ? COLORS.positive : COLORS.negative}
            rightLabel="Annualized"
            rightValue={(royethResult.summary.vsWstEthAnnualized >= 0 ? "+" : "") + fmtPct(royethResult.summary.vsWstEthAnnualized)}
            rightColor={royethResult.summary.vsWstEthAnnualized >= 0 ? COLORS.positive : COLORS.negative}
          />
        </div>

        {/* ── Value Comparison Chart (indexed to 1.0) ── */}
        <Card title="Value vs ETH (normalized to ETH = 1.0)" color={COLORS.accent}>
          {royethChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={480}>
              <LineChart data={royethChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  tickFormatter={d => d.slice(5)}
                  interval={Math.max(1, Math.floor(royethChartData.length / 12))}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  tickFormatter={v => v.toFixed(3)}
                  width={60}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<ChartTooltip formatter={v => v.toFixed(4)} />} />
                <Line type="monotone" dataKey="RoyETH" stroke={COLORS.accent} strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="ETH" stroke={COLORS.ethBlue} strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="wstETH" stroke={COLORS.lidoBlue} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
              No data available. Run scripts/fetch-royeth-data.mjs to generate rate data.
            </div>
          )}
          <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
            {[
              { label: "RoyETH", color: COLORS.accent },
              { label: "ETH", color: COLORS.ethBlue },
              { label: "wstETH", color: COLORS.lidoBlue },
            ].map(l => (
              <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                <div style={{
                  width: 18, height: l.dashed ? 0 : 2,
                  background: l.dashed ? "transparent" : l.color,
                  borderTop: l.dashed ? `2px dashed ${l.color}` : "none",
                }} />
                {l.label}
              </div>
            ))}
          </div>
        </Card>

        {/* ── Actual LTV Chart (Dynamic mode only) ── */}
        {royethConfig.ltvMode === 'dynamic' && actualLtvChartData.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Card title="Actual LTV" color={COLORS.accent}>
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={actualLtvChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={d => d.slice(5)}
                    interval={Math.max(1, Math.floor(actualLtvChartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={v => v.toFixed(1) + "%"}
                    width={60}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip content={<ChartTooltip formatter={v => v.toFixed(2) + "%"} />} />
                  <ReferenceLine
                    y={royethConfig.aaveLTV}
                    stroke={COLORS.textTertiary}
                    strokeDasharray="6 3"
                    strokeWidth={1.5}
                    label={{
                      value: `Target ${royethConfig.aaveLTV}%`,
                      position: "right",
                      fontSize: 10,
                      fill: COLORS.textTertiary,
                      fontFamily: "'Fragment Mono', monospace",
                    }}
                  />
                  <ReferenceLine
                    y={LIQUIDATION_LTV}
                    stroke={COLORS.negative}
                    strokeDasharray="4 2"
                    strokeWidth={1.5}
                    label={{
                      value: `Liquidation ${LIQUIDATION_LTV}%`,
                      position: "right",
                      fontSize: 10,
                      fill: COLORS.negative,
                      fontFamily: "'Fragment Mono', monospace",
                    }}
                  />
                  <Line type="monotone" dataKey="Actual LTV" stroke={COLORS.accent} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
                {[
                  { label: "Actual LTV", color: COLORS.accent },
                  { label: `Target (${royethConfig.aaveLTV}%)`, color: COLORS.textTertiary, dashed: true },
                  { label: `Liquidation (${LIQUIDATION_LTV}%)`, color: COLORS.negative, dashed: true },
                ].map(l => (
                  <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                    <div style={{
                      width: 18, height: l.dashed ? 0 : 2,
                      background: l.dashed ? "transparent" : l.color,
                      borderTop: l.dashed ? `2px dashed ${l.color}` : "none",
                    }} />
                    {l.label}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Trailing 7-Day APY (vs ETH) ── */}
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Card title="Trailing 7-Day APY vs ETH" color={COLORS.accent}>
            {royethApyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={royethApyChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={d => d.slice(5)}
                    interval={Math.max(1, Math.floor(royethApyChartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={v => v.toFixed(1) + "%"}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip formatter={v => v.toFixed(3) + "%"} />} />
                  <Line type="monotone" dataKey="RoyETH APY" stroke={COLORS.accent} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
                Not enough data for 7-day trailing average.
              </div>
            )}
          </Card>
        </div>

        {/* ── Yield Component Breakdown ── */}
        <div style={{ marginBottom: 20 }}>
          <Card title="Yield Component Breakdown" color={COLORS.accent}>
            {royethComponentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={royethComponentChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={d => d.slice(5)}
                    interval={Math.max(1, Math.floor(royethComponentChartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={v => v.toFixed(1) + "%"}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip formatter={v => v.toFixed(3) + "%"} />} />
                  <Line type="monotone" dataKey="Staking" stroke={COLORS.positive} strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="Supply" stroke={COLORS.lidoBlue} strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="Carry" stroke={COLORS.accent} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
                No component data available.
              </div>
            )}
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
              {[
                { label: "Staking Yield", color: COLORS.positive },
                { label: "Supply APY", color: COLORS.lidoBlue },
                { label: "Carry Spread", color: COLORS.accent },
              ].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                  <div style={{ width: 18, height: 2, background: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* ── DSV Yield vs Borrow Rate ── */}
        <div style={{ marginBottom: 20 }}>
          <Card title="DSV Senior Yield vs USDC Borrow Rate" color={COLORS.accent}>
            {royethRatesChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={royethRatesChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={d => d.slice(5)}
                    interval={Math.max(1, Math.floor(royethRatesChartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={v => v.toFixed(1) + "%"}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip formatter={v => v.toFixed(3) + "%"} />} />
                  <Line type="monotone" dataKey="DSV Senior Yield" stroke={COLORS.positive} strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="USDC Borrow Rate" stroke={COLORS.negative} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: COLORS.textTertiary, fontSize: 13 }}>
                No data available.
              </div>
            )}
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
              {[
                { label: "DSV Senior Yield", color: COLORS.positive },
                { label: "USDC Borrow Rate", color: COLORS.negative, dashed: true },
              ].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                  <div style={{
                    width: 18, height: l.dashed ? 0 : 2,
                    background: l.dashed ? "transparent" : l.color,
                    borderTop: l.dashed ? `2px dashed ${l.color}` : "none",
                  }} />
                  {l.label}
                </div>
              ))}
            </div>
          </Card>
        </div>

        </>)}

      </div>
    </div>
  );
}

// ── Metric Card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color = COLORS.ink }) {
  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 10,
      padding: "14px 18px",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
        textTransform: "uppercase", color: COLORS.textTertiary, marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 700,
        fontFamily: "'Fragment Mono', monospace",
        color,
      }}>
        {value}
      </div>
    </div>
  );
}

function DualMetricCard({ title, leftLabel, leftValue, leftColor, rightLabel, rightValue, rightColor }) {
  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 10,
      padding: "14px 18px",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
        textTransform: "uppercase", color: COLORS.textTertiary, marginBottom: 10,
        textAlign: "center",
      }}>
        {title}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{
            fontSize: 9, fontWeight: 600, letterSpacing: "0.06em",
            textTransform: "uppercase", color: COLORS.textTertiary, marginBottom: 4,
          }}>
            {leftLabel}
          </div>
          <div style={{
            fontSize: 20, fontWeight: 700,
            fontFamily: "'Fragment Mono', monospace",
            color: leftColor,
          }}>
            {leftValue}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 9, fontWeight: 600, letterSpacing: "0.06em",
            textTransform: "uppercase", color: COLORS.textTertiary, marginBottom: 4,
          }}>
            {rightLabel}
          </div>
          <div style={{
            fontSize: 20, fontWeight: 700,
            fontFamily: "'Fragment Mono', monospace",
            color: rightColor,
          }}>
            {rightValue}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared styles ───────────────────────────────────────────────────────────

const colHeaderStyle = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: COLORS.textTertiary,
  fontFamily: "'Inter', sans-serif",
};

const thStyle = {
  textAlign: "left",
  padding: "8px 6px",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: COLORS.textTertiary,
  fontFamily: "'Inter', sans-serif",
  whiteSpace: "normal",
};

const thRightStyle = {
  ...thStyle,
  textAlign: "right",
  minWidth: 48,
};

const tdStyle = {
  padding: "8px 6px",
  fontSize: 12,
  lineHeight: 1.4,
};
